
import { Injectable } from '@angular/core';
import { CanDeactivate } from '@angular/router';
import { Observable } from 'rxjs';
import { LoggingService } from '../utils/logging.service';

/**
 * Interface für Komponenten, die mit dem Route-Guard geschützt werden sollen.
 * Wird z.B. von PortalModule implementiert.
 */
export interface ComponentCanDeactivate {
    /**
     * Öffne den Dialog zur Bestätigung, ob die aktuelle schmuztige Seite verwerfen werden soll
     * @returns Ergebnis des Dialogs als Observable. True, falls die Seite verlassen werden soll.
     */
    openDeactivateConfirmation: () => Observable<boolean>;
}

/**
 * CanDeactivate-Guard entscheidet ob der aktuelle Route verlassen werden darf.
 * Wenn die Property "isDirty" true gesetzt wurde, wird beim Wegnavigieren einen modalen Dialog geöffnet.
 * Der Nutzer kann dann entscheiden, ob er die Seite verlassen möchte.
 * Der Wert von "isDirty" kann von einer Komponente mit der Methode {@link #setDirty} gesetzt werden.
 *
 */
@Injectable()
export class UnsavedChangesGuard implements CanDeactivate<ComponentCanDeactivate> {

    public isDirty: boolean = false;

    constructor(private log: LoggingService) {}

    /**
     * Implementierung der canDeactivate-Methode. Beim Navigieren wird der Rückgabewert dieser Methode überprüft.
     * Wenn true, wird die Navigation fortgesetzt, sonst wird sie abgebrochen.
     */
    canDeactivate(component: ComponentCanDeactivate): boolean | Observable<boolean> {
        if (this.isDirty) {
            // Modaler Dialog öffnen und Ergebnis des Dialogs behandeln
            const deactivateConfirmation = component.openDeactivateConfirmation();
            deactivateConfirmation.subscribe(result => {
                if (result) {
                    // "isDirty" wieder falsch setzen, damit bei allen weiteren Navigationen
                    // der Bestätigungsdialog nicht mehr angezeigt werden kann.
                    this.setDirty(false);
                }
            })
            return deactivateConfirmation;
        }
        return true
    }

    /**
     * Setzt den Wert von isDirty
     *
     * @param isDirty
     */
    public setDirty(isDirty: boolean) {
        this.isDirty = isDirty;
        this.log.debug(`UnsavedChangesGuard#setDirty: Dirty-Event ausgelöst (${this.isDirty})`);
    }
}
